import React, { useLayoutEffect, useRef } from 'react';
import { AudioTrackInfo } from 'components/organisms/gen-ai-audio-tool/GenAITool';
import { Button } from 'components/atoms';
import * as Styled from './AudioCardSection.styled';
import { AudioCard } from './AudioCard';
import { useCarouselScroll } from './useCarouselScroll';

export interface Audio {
  name: string;
  image: string;
  audio: string;
  audioId: string;
}

export interface AudioCardSectionProps {
  selected: number;
  /* eslint-disable no-unused-vars */
  onChange: (idx: number) => void;
  audioTracks: Audio[];
  currentAudioId: string;
  title: string;
  caption: string;
  type?: 'voice' | 'music';
  handlePlayAudio: (
    trackInfo: Pick<AudioTrackInfo, 'id' | 'audioElement'> & {
      playFromStart?: boolean;
    },
  ) => void;
  handlePauseAudio: () => void;
  handleAudioEnded: () => void;
}

export const AudioCardSection: React.FC<AudioCardSectionProps> = ({
  selected,
  onChange,
  audioTracks,
  currentAudioId,
  title,
  caption,
  type = 'voice',
  handlePlayAudio,
  handlePauseAudio,
  handleAudioEnded,
}) => {
  const audioRefs = useRef<(HTMLAudioElement | null)[]>([]);
  const {
    rowRef,
    canScrollLeft,
    canScrollRight,
    scrollToDirection,
    checkScrollState,
    isScrollInitialized,
  } = useCarouselScroll();

  useLayoutEffect(() => {
    checkScrollState();
  }, [audioTracks, checkScrollState]);

  const handlePlayClick = (idx: number) => (e: React.MouseEvent) => {
    e.stopPropagation();
    if (currentAudioId === audioTracks[idx].audioId && audioRefs.current[idx]) {
      if (audioRefs.current[idx]?.paused) {
        handlePlayAudio({
          id: audioTracks[idx].audioId,
          audioElement: audioRefs.current[idx],
        });
      } else {
        handlePauseAudio();
      }
    } else {
      handlePlayAudio({
        id: audioTracks[idx].audioId,
        audioElement: audioRefs.current[idx],
        playFromStart: true,
      });
    }
  };

  return (
    <Styled.Section>
      <Styled.Title>{title}</Styled.Title>
      <Styled.RowWrapper>
        <Styled.Row ref={rowRef}>
          {audioTracks.map((audioTrack, idx) => (
            <AudioCard
              key={audioTrack.name}
              audio={audioTrack}
              selected={selected === idx}
              onSelect={() => onChange(idx)}
              playing={
                currentAudioId === audioTracks[idx].audioId &&
                !audioRefs.current[idx]?.paused
              }
              onPlayClick={handlePlayClick(idx)}
              onAudioEnded={handleAudioEnded}
              audioRef={el => {
                audioRefs.current[idx] = el;
                return el;
              }}
              type={type}
            />
          ))}
        </Styled.Row>
        <Styled.ArrowButtonWrapper
          $direction="left"
          $isVisible={canScrollLeft}
          $isInitialized={isScrollInitialized}
        >
          {/* @ts-expect-error - Property '[SymbolConstructor.iterator]' is missing in type '{ type: string; onClick: (e: MouseEvent<Element, MouseEvent>) => void; }' but required in type 'Pick<string, unique symbol>' */}
          <Button type="roundLeftArrow" onClick={scrollToDirection('left')} />
        </Styled.ArrowButtonWrapper>
        <Styled.ArrowButtonWrapper
          $direction="right"
          $isVisible={canScrollRight}
          $isInitialized={isScrollInitialized}
        >
          {/* @ts-expect-error - Property '[SymbolConstructor.iterator]' is missing in type '{ type: string; onClick: (e: MouseEvent<Element, MouseEvent>) => void; }' but required in type 'Pick<string, unique symbol>' */}
          <Button type="roundRightArrow" onClick={scrollToDirection('right')} />
        </Styled.ArrowButtonWrapper>
      </Styled.RowWrapper>
      <Styled.Caption>{caption}</Styled.Caption>
    </Styled.Section>
  );
};
